import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { TbAlertTriangle, TbChevronDown, TbChevronRight, TbChevronUp, TbPlus, TbTrash, TbUserPlus } from "react-icons/tb";
import { useCEPData } from "../../../../../Hooks/CEP";
import { formatCountryOptionLabel } from "../../../../../Services/baasPlatformOptionsApi";
import {
  digitsOnly,
  formatLocalPhoneNumberDisplay,
  formatPhoneDialOptionLabel,
  formatPhonePartsForDisplay,
} from "../../../../../Services/baasPhoneParts";
import {
  BAAS_INDUSTRY_OPTIONS,
  getOwnerDisplayName,
  getOwnershipTotal,
  getStepCompletionStatus,
  isStepSkippedForIndustry,
  MAX_BENEFICIAL_OWNERS,
} from "../../../../../Services/baasBusinessAccountApi";
import {
  getStateOptions,
  hasAnyOwnerIdentityDocNumber,
  hasIdentityDocNumber,
  isBrCountry,
  isUsCountry,
} from "../baasEntidadeCreateVisibility";

export const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

export const textareaClass =
  "min-h-[80px] w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

export const Field = ({ label, children, required = false, optional = false }) => (
  <label className="block min-w-0 text-sm">
    <span className="mb-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 leading-snug text-gray-600 dark:text-gray-400">
      {label}
      {required ? (
        <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-[#b8860b]">
          {intl.get("baas.entidades.create.required")}
        </span>
      ) : null}
      {optional ? (
        <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-gray-400">
          {intl.get("baas.entidades.create.optional")}
        </span>
      ) : null}
    </span>
    {children}
  </label>
);

/**
 * Split phone: country dial (DDI) + area code (DDD) + local number.
 * `value` shape: { countryDialCode, areaCode, number }
 */
export const PhonePartsFields = ({
  value,
  onChange,
  dialOptions = [],
  required = false,
  optional = false,
  disabled = false,
  readOnly = false,
  className = "",
  fieldClassName = inputClass,
}) => {
  const locked = disabled || readOnly;
  const phone =
    value && typeof value === "object"
      ? value
      : { countryDialCode: "55", areaCode: "", number: "" };
  const dial = digitsOnly(phone.countryDialCode) || "55";
  const localDisplay = formatLocalPhoneNumberDisplay(phone.number, dial);
  const localPlaceholder = dial === "55" ? "99999-9999" : "999990000";

  return (
    <div
      className={`grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1.15fr)_minmax(0,0.75fr)_minmax(0,1.35fr)] ${className}`.trim()}
    >
      <div className="min-w-0">
        <Field label={intl.get("baas.entidades.personal.phoneCountry")} required={required} optional={optional}>
          <select
            className={fieldClassName}
            value={phone.countryDialCode || ""}
            onChange={(event) => onChange?.({ countryDialCode: event.target.value.replace(/\D/g, "") })}
            required={required}
            disabled={locked}
          >
            <option value="" disabled>
              {intl.get("baas.entidades.personal.selectPhoneCountry")}
            </option>
            {dialOptions.map((option) => (
              <option key={`${option.dial}-${option.code}`} value={option.dial}>
                {formatPhoneDialOptionLabel(option)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="min-w-0">
        <Field label={intl.get("baas.entidades.personal.phoneAreaCode")} required={required} optional={optional}>
          <input
            type="tel"
            inputMode="numeric"
            className={fieldClassName}
            value={phone.areaCode || ""}
            onChange={(event) => onChange?.({ areaCode: event.target.value.replace(/\D/g, "").slice(0, 4) })}
            placeholder="11"
            required={required}
            disabled={locked}
            readOnly={readOnly}
          />
        </Field>
      </div>
      <div className="min-w-0">
        <Field label={intl.get("baas.entidades.personal.phoneLocalNumber")} required={required} optional={optional}>
          <input
            type="tel"
            inputMode="numeric"
            className={fieldClassName}
            value={localDisplay}
            onChange={(event) => {
              const maxLen = dial === "55" ? 9 : 12;
              onChange?.({ number: digitsOnly(event.target.value).slice(0, maxLen) });
            }}
            placeholder={localPlaceholder}
            required={required}
            disabled={locked}
            readOnly={readOnly}
          />
        </Field>
      </div>
    </div>
  );
};

export const StepHeader = ({ title, description }) => (
  <div className="mb-1">
    <h3 className="text-base font-semibold text-brand-dark dark:text-gray-100">{title}</h3>
    {description ? (
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
    ) : null}
  </div>
);

const STEP_STATUS_DOT_CLASS = {
  complete: "bg-emerald-500",
  pending: "bg-red-500",
  optional: "bg-amber-400",
};

const StepStatusIndicator = ({ status }) => {
  if (status === "skipped") {
    return (
      <span
        className="inline-block h-0.5 w-2 shrink-0 rounded-full bg-gray-300 dark:bg-gray-600"
        aria-hidden
      />
    );
  }

  const dotClass = STEP_STATUS_DOT_CLASS[status];
  if (!dotClass) {
    return null;
  }

  return <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} aria-hidden />;
};

const buildStepTabAriaLabel = (step, status, stepLabelPrefix = "baas.entidades.create.step") => {
  const label = intl.get(`${stepLabelPrefix}.${step}`);
  const statusKey = `baas.entidades.create.stepStatus${status.charAt(0).toUpperCase()}${status.slice(1)}`;
  const statusLabel = intl.get(statusKey);
  return `${label} — ${statusLabel}`;
};

const handleWizardStepsWheel = (event) => {
  if (event.shiftKey) {
    return;
  }

  const { deltaY, deltaX, currentTarget } = event;
  if (Math.abs(deltaY) <= Math.abs(deltaX)) {
    return;
  }

  const maxScrollLeft = currentTarget.scrollWidth - currentTarget.clientWidth;
  if (maxScrollLeft <= 0) {
    return;
  }

  event.preventDefault();
  const scrollParent = currentTarget.closest("main");
  if (scrollParent) {
    scrollParent.scrollTop += deltaY;
  }
};

export const WizardStepTabs = ({
  steps,
  activeIndex,
  onStepClick,
  form,
  visible,
  industryRequirements,
  instanceId,
  getStepStatus,
  stepLabelPrefix = "baas.entidades.create.step",
}) => (
  <div
    className="baas-wizard-steps flex min-w-0 gap-2 pb-1"
    role="tablist"
    aria-label={intl.get("baas.entidades.create.stepNavigation")}
    onWheel={handleWizardStepsWheel}
  >
    {steps.map((step, index) => {
      const status = getStepStatus
        ? getStepStatus(step, form, { visible, industryRequirements, instanceId })
        : getStepCompletionStatus(step, form, {
            visible,
            industryRequirements,
            instanceId,
          });
      const isActive = index === activeIndex;
      const skipped = status === "skipped";

      return (
        <button
          key={step}
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-current={isActive ? "step" : undefined}
          aria-label={buildStepTabAriaLabel(step, status, stepLabelPrefix)}
          onClick={() => onStepClick(index)}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ${
            skipped
              ? "bg-gray-50 text-gray-400 line-through dark:bg-brand-dark-elevated dark:text-gray-600"
              : isActive
                ? "bg-[#e7a73f]/15 text-[#b8860b] ring-1 ring-[#e7a73f]/30"
                : status === "complete"
                  ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-400 dark:hover:bg-emerald-950/60"
                  : status === "pending"
                    ? "bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-400 dark:hover:bg-red-950/50"
                    : status === "optional"
                      ? "border border-dashed border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-400"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-brand-dark-elevated dark:text-gray-300 dark:hover:bg-brand-dark-surface"
          }`}
          title={
            skipped
              ? intl.get("baas.entidades.create.stepSkipped")
              : status === "optional"
                ? intl.get("baas.entidades.create.optionalSection")
                : undefined
          }
        >
          <span>{intl.get(`${stepLabelPrefix}.${step}`)}</span>
          <StepStatusIndicator status={status} />
        </button>
      );
    })}
  </div>
);

export const SectionHeading = ({ children }) => (
  <p className="border-b border-gray-100 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:border-brand-dark-border">
    {children}
  </p>
);

export const ConditionalHelper = ({ messageKey }) =>
  messageKey ? (
    <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-brand-dark-surface dark:text-gray-400">
      {intl.get(messageKey)}
    </p>
  ) : null;

export const getIndustryOptionLabel = (value) =>
  intl.get(`baas.entidades.create.options.industry.${value}`);

export const IndustrySelectField = ({ value, onChange, required = false }) => (
  <Field label={intl.get("baas.entidades.create.industry")} required={required}>
    <select
      className={inputClass}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      required={required}
    >
      {BAAS_INDUSTRY_OPTIONS.map((industry) => (
        <option key={industry} value={industry}>
          {getIndustryOptionLabel(industry)}
        </option>
      ))}
    </select>
  </Field>
);

const RISK_BADGE_STYLES = {
  high: "border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400",
  medium:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-400",
  low: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-400",
};

export const IndustryRiskBadge = ({ riskLevel, industry }) => {
  if (!riskLevel) {
    return null;
  }

  const labelKey = `baas.entidades.create.industryRisk${riskLevel.charAt(0).toUpperCase()}${riskLevel.slice(1)}`;
  const detailKey =
    riskLevel === "high" && industry === "financial_services"
      ? "baas.entidades.create.fintechFullKycRequired"
      : null;

  return (
    <div className={`rounded-lg border px-3 py-2 text-xs ${RISK_BADGE_STYLES[riskLevel] ?? RISK_BADGE_STYLES.medium}`}>
      <p className="font-semibold">{intl.get(labelKey)}</p>
      {detailKey ? <p className="mt-1 opacity-90">{intl.get(detailKey)}</p> : null}
    </div>
  );
};

export const EntityHighRiskBanner = ({ reasons = [], compact = false }) => {
  if (!reasons.length) {
    return null;
  }

  return (
    <div
      className={`rounded-lg border border-red-200 bg-red-50 text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300 ${
        compact ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm"
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-2">
        <TbAlertTriangle className="mt-0.5 shrink-0" size={compact ? 16 : 18} aria-hidden />
        <div>
          <p className="font-semibold">{intl.get("baas.entidades.create.highRiskBannerTitle")}</p>
          <p className="mt-1 opacity-90">{intl.get("baas.entidades.create.highRiskBannerMessage")}</p>
          <ul className="mt-2 list-inside list-disc space-y-0.5 opacity-90">
            {reasons.map((reason) => (
              <li key={reason}>{intl.get(`baas.entidades.create.highRiskReason.${reason}`)}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export const OptionalSectionNotice = () => (
  <p className="rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-500 dark:border-brand-dark-border dark:text-gray-400">
    {intl.get("baas.entidades.create.optionalSection")}
  </p>
);

export const buildPersonDetailPath = (entityId) =>
  `/baas/entidades/${encodeURIComponent(String(entityId || "").trim())}`;

const PERSON_LINK_CLASS =
  "pointer-events-auto relative z-10 inline-flex cursor-pointer items-center text-xs font-semibold text-[#b8860b] underline underline-offset-2 hover:text-[#e7a73f] dark:text-brand-gold";

/** Clickable console link to a Person entity (`/baas/entidades/pers_…`). Survives parent `pointer-events-none`. */
export const PersonConsoleLink = ({ persId, children, className = "" }) => {
  const id = String(persId || "").trim();
  if (!id.toLowerCase().startsWith("pers_")) {
    return null;
  }

  return (
    <Link to={buildPersonDetailPath(id)} className={`${PERSON_LINK_CLASS} ${className}`.trim()}>
      {children}
    </Link>
  );
};

export const OwnershipTotalBanner = ({ owners }) => {
  const total = getOwnershipTotal(owners);
  const exceeds = total > 100;
  const incomplete = total > 0 && total !== 100;

  if (total <= 0 && !exceeds) {
    return null;
  }

  return (
    <div
      className={`rounded-lg border px-3 py-2 text-xs ${
        exceeds
          ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400"
          : incomplete
            ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-400"
            : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-400"
      }`}
    >
      <p className="font-medium">
        {intl.get("baas.entidades.create.ownershipTotal", { total })}
      </p>
      {exceeds ? (
        <p className="mt-1">{intl.get("baas.entidades.create.ownershipExceeds100")}</p>
      ) : incomplete ? (
        <p className="mt-1">{intl.get("baas.entidades.create.ownershipMustEqual100")}</p>
      ) : null}
    </div>
  );
};

export const OwnerSelectorBar = ({
  owners,
  activeIndex,
  onSelect,
  onAdd,
  onAddExisting,
  onRemove,
  maxOwners = MAX_BENEFICIAL_OWNERS,
  readOnly = false,
  canRemoveIndex,
}) => {
  const getOwnerTabLabel = (owner, index) => {
    const name = getOwnerDisplayName(owner, index);
    return name ?? intl.get("baas.entidades.create.ownerNumber", { number: index + 1 });
  };

  const canAdd = !readOnly && owners.length < maxOwners;
  const canRemoveAt = (index) => {
    if (typeof canRemoveIndex === "function") {
      return canRemoveIndex(index);
    }
    return !readOnly && owners.length > 1;
  };
  const addButtonClass =
    "inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:border-[#e7a73f] hover:text-[#b8860b] dark:border-brand-dark-border dark:text-gray-400 dark:hover:border-[#e7a73f]/50 dark:hover:text-[#e7a73f]";

  return (
    <div className="pointer-events-auto space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {owners.map((owner, index) => {
          const isActive = index === activeIndex;
          const label = getOwnerTabLabel(owner, index);
          const showRemove = canRemoveAt(index);
          const tabClass = `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${
            showRemove ? "rounded-l-full" : "rounded-full"
          } ${
            isActive
              ? "bg-[#e7a73f]/15 text-[#b8860b] ring-1 ring-[#e7a73f]/30"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-brand-dark-elevated dark:text-gray-300 dark:hover:bg-brand-dark-surface"
          }`;

          return (
            <div key={`owner-tab-${index}`} className="inline-flex items-stretch">
              <button
                type="button"
                onClick={() => onSelect(index)}
                className={tabClass}
                aria-current={isActive ? "true" : undefined}
              >
                {label}
                {owner.isControlPerson ? (
                  <span className="rounded-full bg-gray-200/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:bg-brand-dark-surface dark:text-gray-400">
                    {intl.get("baas.entidades.create.owner.controlPersonShort")}
                  </span>
                ) : null}
                {owner.isBeneficialOwner ? (
                  <span className="rounded-full bg-emerald-100/90 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                    {intl.get("baas.entidades.create.owner.isBeneficialOwnerShort")}
                  </span>
                ) : null}
              </button>
              {showRemove ? (
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className={`inline-flex items-center rounded-r-full border-l px-2 py-1.5 text-red-500 transition hover:bg-red-50 dark:hover:bg-red-950/30 ${
                    isActive
                      ? "border-[#e7a73f]/30 bg-[#e7a73f]/15"
                      : "border-gray-200 bg-gray-100 dark:border-brand-dark-border dark:bg-brand-dark-elevated"
                  }`}
                  aria-label={intl.get("baas.entidades.create.removeOwner", { name: label })}
                >
                  <TbTrash size={14} aria-hidden />
                </button>
              ) : null}
            </div>
          );
        })}

        {canAdd ? (
          <button type="button" onClick={onAdd} className={addButtonClass}>
            <TbPlus size={14} aria-hidden />
            {intl.get("baas.entidades.create.addOwner")}
          </button>
        ) : null}
        {canAdd && onAddExisting ? (
          <button type="button" onClick={onAddExisting} className={addButtonClass}>
            <TbUserPlus size={14} aria-hidden />
            {intl.get("baas.entidades.create.addExistingPerson")}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export const OwnerReviewCard = ({ owner, index, visible, enumLabel, industryRequirements }) => (
  <div className="rounded-lg border border-gray-100 bg-white p-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated">
    <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
      {getOwnerDisplayName(owner, index) ??
        intl.get("baas.entidades.create.ownerNumber", { number: index + 1 })}
      {owner.ownershipPercentage !== "" && owner.ownershipPercentage != null ? (
        <span className="ml-2 text-xs font-normal text-gray-500">
          · {owner.ownershipPercentage}%
        </span>
      ) : null}
    </p>
    <div className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-300">
      {owner?.persId ? (
        <div className="space-y-1">
          <p className="font-mono text-xs text-emerald-700 dark:text-emerald-400">
            {intl.get("baas.entidades.create.existingPersonLinkedShort", { id: owner.persId })}
          </p>
          <PersonConsoleLink persId={owner.persId}>
            {intl.get("baas.entidades.create.openPersonInConsole")}
          </PersonConsoleLink>
        </div>
      ) : null}
      <p>{owner.email || "—"}</p>
      <p>{formatPhonePartsForDisplay(owner.phone)}</p>
      {visible.ssn && owner.ssn ? <p>{owner.ssn}</p> : null}
      <p>
        {owner.occupation
          ? `${enumLabel("employmentStatus", owner.employmentStatus)} · ${owner.occupation}`
          : "—"}
      </p>
      <p className="text-xs text-gray-500">
        {[
          owner.isBeneficialOwner
            ? intl.get("baas.entidades.create.owner.isBeneficialOwner")
            : null,
          owner.isControlPerson
            ? intl.get("baas.entidades.create.owner.controlPerson")
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || intl.get("baas.entidades.create.owner.isBeneficialOwnerMissing")}
      </p>
      <p className="text-xs text-gray-500">{formatAddressForReview(owner.address)}</p>
      {!isStepSkippedForIndustry("ownerIdentity", industryRequirements) &&
      hasAnyOwnerIdentityDocNumber(owner) ? (
        <p className="text-xs text-gray-500">
          {intl.get("baas.entidades.create.identity.passport")}: {owner.passport.number || "—"}
        </p>
      ) : null}
      {!isStepSkippedForIndustry("ownerAccount", industryRequirements) &&
      owner.purposeOfAccount?.length ? (
        <p className="text-xs text-gray-500">
          {owner.purposeOfAccount.map((item) => enumLabel("purposeOfAccount", item)).join(", ")}
        </p>
      ) : null}
    </div>
  </div>
);

const formatAddressForReview = (address) => {
  if (!address?.line1?.trim()) {
    return "—";
  }
  const line2 = address.line2 ? `, ${address.line2}` : "";
  return `${address.line1}${line2}, ${address.city}, ${address.state} ${address.postalCode} · ${address.countryCode}`;
};

export const CheckboxField = ({ label, checked, onChange, disabled = false, hint }) => (
  <div className="space-y-1">
    <label
      className={`flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 ${disabled ? "opacity-70" : ""}`}
    >
      <input type="checkbox" checked={Boolean(checked)} onChange={onChange} disabled={disabled} />
      {label}
    </label>
    {hint ? <p className="pl-6 text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
  </div>
);

export const OwnerParticipationFlags = ({
  owner,
  onChange,
  locked = false,
  lastBeneficialOwner = false,
}) => {
  const boHint = lastBeneficialOwner
    ? intl.get("baas.entidades.create.owner.isBeneficialOwnerLockedHint")
    : intl.get("baas.entidades.create.owner.isBeneficialOwnerHint");

  return (
    <div className="space-y-2 rounded-lg border border-gray-100 bg-gray-50/80 p-3 dark:border-brand-dark-border dark:bg-brand-dark-surface">
      <CheckboxField
        label={intl.get("baas.entidades.create.owner.isBeneficialOwner")}
        checked={Boolean(owner.isBeneficialOwner)}
        onChange={(event) => {
          if (!event.target.checked && lastBeneficialOwner) return;
          onChange({ isBeneficialOwner: event.target.checked });
        }}
        disabled={locked}
        hint={boHint}
      />
      <CheckboxField
        label={intl.get("baas.entidades.create.owner.controlPerson")}
        checked={Boolean(owner.isControlPerson)}
        onChange={(event) => {
          const checked = event.target.checked;
          onChange(
            checked
              ? { isControlPerson: true, isBeneficialOwner: true }
              : { isControlPerson: false },
          );
        }}
        disabled={locked}
      />
    </div>
  );
};

const normalizeMultiCheckboxValues = (values) => {
  if (Array.isArray(values)) {
    return values.filter((item) => item != null && String(item).length > 0);
  }
  if (typeof values === "string" && values.trim()) {
    return values
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

/**
 * Purpose / multi-select chips. Uses buttons (not sr-only checkboxes): absolute
 * sr-only inputs inside overflow scroll parents (private-app main) can focus-scroll
 * the page to the top and look like the form “broke” / jumped to Produtos.
 */
export const MultiCheckboxField = ({
  label,
  options = [],
  values,
  onChange,
  labelKeyPrefix,
  required = false,
  disabled = false,
}) => {
  const selectedValues = normalizeMultiCheckboxValues(values);

  return (
    <div className="relative text-sm">
      <span className="mb-2 flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
        {label}
        {required ? (
          <span className="text-[10px] font-medium uppercase tracking-wide text-[#b8860b]">
            {intl.get("baas.entidades.create.required")}
          </span>
        ) : null}
      </span>
      <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
        {options.map((option) => {
          const selected = selectedValues.includes(option);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={selected}
              disabled={disabled}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e7a73f]/40 ${
                disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
              } ${
                selected
                  ? "border-[#e7a73f] bg-[#e7a73f]/10 text-[#b8860b]"
                  : "border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-300"
              }`}
              onClick={() => {
                if (disabled) return;
                onChange(
                  selected
                    ? selectedValues.filter((value) => value !== option)
                    : [...selectedValues, option],
                );
              }}
            >
              {labelKeyPrefix ? intl.get(`${labelKeyPrefix}.${option}`) : option}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const CollapsibleSection = ({
  title,
  description,
  defaultOpen = false,
  optional = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  return (
    <div className="rounded-xl border border-gray-100 dark:border-brand-dark-border">
      <button
        type="button"
        className="pointer-events-auto flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <div>
          <p className="flex items-center gap-2 text-sm font-medium text-brand-dark dark:text-gray-100">
            {title}
            {optional ? (
              <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                {intl.get("baas.entidades.create.optional")}
              </span>
            ) : null}
          </p>
          {description ? (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
          ) : null}
        </div>
        {open ? (
          <TbChevronDown size={18} className="shrink-0 text-gray-400" aria-hidden />
        ) : (
          <TbChevronRight size={18} className="shrink-0 text-gray-400" aria-hidden />
        )}
      </button>
      {open ? <div className="space-y-4 border-t border-gray-100 p-4 dark:border-brand-dark-border">{children}</div> : null}
    </div>
  );
};

export const StateOfIncorporationField = ({ countryCode, value, onChange, required = false }) => {
  const stateOptions = getStateOptions(countryCode);
  const label = isUsCountry(countryCode)
    ? intl.get("baas.entidades.create.stateOfIncorporation")
    : isBrCountry(countryCode)
      ? intl.get("baas.entidades.create.stateOfIncorporationBr")
      : intl.get("baas.entidades.create.stateOfIncorporation");

  if (stateOptions.length > 0) {
    return (
      <Field label={label} required={required}>
        <select className={inputClass} value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">{intl.get("baas.entidades.create.selectState")}</option>
          {stateOptions.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  return (
    <Field label={label} optional={!required}>
      <input
        type="text"
        className={inputClass}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
};

export const CountrySelectField = ({
  label,
  value,
  onChange,
  countryOptions = [],
  required = false,
  optional = false,
  selectLabelKey = "baas.entidades.create.selectCountry",
  disabled = false,
}) => (
  <Field label={label} required={required} optional={optional}>
    <select
      className={inputClass}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      required={required}
      disabled={disabled}
    >
      <option value="">{intl.get(selectLabelKey)}</option>
      {countryOptions.map((country) => (
        <option key={country.code} value={country.code}>
          {formatCountryOptionLabel(country)}
        </option>
      ))}
    </select>
  </Field>
);

export const StateOrTextField = ({
  label,
  countryCode,
  value,
  onChange,
  required = false,
  optional = false,
  selectStateKey = "baas.entidades.create.selectState",
  disabled = false,
}) => {
  const stateOptions = getStateOptions(countryCode);

  if (stateOptions.length > 0) {
    return (
      <Field label={label} required={required} optional={optional}>
        <select
          className={inputClass}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          disabled={disabled}
        >
          <option value="">{intl.get(selectStateKey)}</option>
          {stateOptions.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  return (
    <Field label={label} required={required} optional={optional}>
      <input
        type="text"
        className={inputClass}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        disabled={disabled}
        readOnly={disabled}
      />
    </Field>
  );
};

const CepLookupHint = ({ loading, error }) => {
  if (loading) {
    return (
      <p className="mt-1 text-xs text-gray-400">{intl.get("baas.entidades.create.cepLoading")}</p>
    );
  }

  if (error) {
    return (
      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
        {intl.get("baas.entidades.create.cepError")}
      </p>
    );
  }

  return null;
};

const useBrCepAutofill = ({ countryCode, onChange, brShape = "br" }) => {
  const { fetchCEPData, requestError, requestLoading, resetCEP } = useCEPData();

  const applyCepValues = (values, currentPostalCode) => {
    if (!values?.logradouro && !values?.localidade) {
      return;
    }

    const patch =
      brShape === "br"
        ? {
            line1: values.logradouro || undefined,
            neighborhood: values.bairro || undefined,
            city: values.localidade || undefined,
            state: values.uf || undefined,
            countryCode: "BR",
            postalCode: values.cep || currentPostalCode,
          }
        : {
            line1: values.logradouro || undefined,
            line2: values.complemento || undefined,
            city: values.localidade || undefined,
            state: values.uf || undefined,
            countryCode: "BR",
            postalCode: values.cep || currentPostalCode,
          };

    onChange(
      Object.fromEntries(Object.entries(patch).filter(([, entry]) => entry != null && entry !== "")),
    );
  };

  const tryFetchCep = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (digits.length !== 8) {
      return;
    }
    if (countryCode && !isBrCountry(countryCode)) {
      return;
    }

    fetchCEPData({ cep: digits }).then((values) => {
      if (values) {
        applyCepValues(values, value);
      }
    });
  };

  useEffect(() => {
    resetCEP();
  }, [countryCode, resetCEP]);

  return { tryFetchCep, requestError, requestLoading };
};

export const BrAddressFieldsGrid = ({
  address,
  onChange,
  required = false,
  labelPrefix = "baas.entidades.personal.address",
  countryOptions = [],
}) => {
  const { tryFetchCep, requestError, requestLoading } = useBrCepAutofill({
    countryCode: address.countryCode,
    onChange,
    brShape: "br",
  });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label={intl.get(`${labelPrefix}.postalCode`)} required={required}>
        <input
          type="text"
          className={inputClass}
          value={address.postalCode}
          onChange={(event) => {
            onChange({ postalCode: event.target.value });
            tryFetchCep(event.target.value);
          }}
          onBlur={() => tryFetchCep(address.postalCode)}
          required={required}
        />
        <CepLookupHint loading={requestLoading} error={requestError} />
      </Field>
      <CountrySelectField
        label={intl.get(`${labelPrefix}.countryCode`)}
        value={address.countryCode}
        onChange={(value) => onChange({ countryCode: value })}
        countryOptions={countryOptions}
        required={required}
        selectLabelKey="baas.entidades.personal.selectCountry"
      />
      <Field label={intl.get(`${labelPrefix}.line1`)} required={required}>
        <input
          type="text"
          className={inputClass}
          value={address.line1}
          onChange={(event) => onChange({ line1: event.target.value })}
          required={required}
        />
      </Field>
      <Field label={intl.get(`${labelPrefix}.number`)} required={required}>
        <input
          type="text"
          className={inputClass}
          value={address.number}
          onChange={(event) => onChange({ number: event.target.value })}
          required={required}
        />
      </Field>
      <Field label={intl.get(`${labelPrefix}.neighborhood`)} required={required}>
        <input
          type="text"
          className={inputClass}
          value={address.neighborhood}
          onChange={(event) => onChange({ neighborhood: event.target.value })}
          required={required}
        />
      </Field>
      <Field label={intl.get(`${labelPrefix}.city`)} required={required}>
        <input
          type="text"
          className={inputClass}
          value={address.city}
          onChange={(event) => onChange({ city: event.target.value })}
          required={required}
        />
      </Field>
      <StateOrTextField
        label={intl.get(`${labelPrefix}.state`)}
        countryCode={address.countryCode}
        value={address.state}
        onChange={(value) => onChange({ state: value })}
        required={required}
        selectStateKey="baas.entidades.personal.selectState"
      />
    </div>
  );
};

export const AddressFieldsGrid = ({
  address,
  onChange,
  required = false,
  countryOptions = [],
}) => {
  const { tryFetchCep, requestError, requestLoading } = useBrCepAutofill({
    countryCode: address.countryCode,
    onChange,
    brShape: "international",
  });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label={intl.get("baas.entidades.create.address.postalCode")} required={required}>
        <input
          type="text"
          className={inputClass}
          value={address.postalCode}
          onChange={(event) => {
            onChange({ postalCode: event.target.value });
            tryFetchCep(event.target.value);
          }}
          onBlur={() => tryFetchCep(address.postalCode)}
          required={required}
        />
        {isBrCountry(address.countryCode) || !address.countryCode ? (
          <CepLookupHint loading={requestLoading} error={requestError} />
        ) : null}
      </Field>
      <CountrySelectField
        label={intl.get("baas.entidades.create.address.countryCode")}
        value={address.countryCode}
        onChange={(value) => onChange({ countryCode: value })}
        countryOptions={countryOptions}
        required={required}
      />
      <Field label={intl.get("baas.entidades.create.address.line1")} required={required}>
        <input
          type="text"
          className={inputClass}
          value={address.line1}
          onChange={(event) => onChange({ line1: event.target.value })}
          required={required}
        />
      </Field>
      <Field label={intl.get("baas.entidades.create.address.line2")} optional>
        <input
          type="text"
          className={inputClass}
          value={address.line2}
          onChange={(event) => onChange({ line2: event.target.value })}
        />
      </Field>
      <Field label={intl.get("baas.entidades.create.address.city")} required={required}>
        <input
          type="text"
          className={inputClass}
          value={address.city}
          onChange={(event) => onChange({ city: event.target.value })}
          required={required}
        />
      </Field>
      <StateOrTextField
        label={intl.get("baas.entidades.create.address.state")}
        countryCode={address.countryCode}
        value={address.state}
        onChange={(value) => onChange({ state: value })}
        required={required}
      />
    </div>
  );
};

export const IdentityDocFields = ({
  title,
  doc,
  onChange,
  compact = false,
  countryOptions = [],
  disabled = false,
  /** When set (e.g. "BR"), country select is locked and value is forced. */
  forcedCountryCode = null,
}) => {
  const docActive = hasIdentityDocNumber(doc);
  const lockedCountry = forcedCountryCode
    ? String(forcedCountryCode).trim().toUpperCase()
    : null;

  useEffect(() => {
    if (!lockedCountry) return;
    if (String(doc?.countryCode || "").toUpperCase() === lockedCountry) return;
    onChange?.({ countryCode: lockedCountry });
  }, [lockedCountry, doc?.countryCode, onChange]);

  return (
    <div
      className={
        compact
          ? "space-y-4"
          : "space-y-4 rounded-xl border border-gray-100 p-4 dark:border-brand-dark-border"
      }
    >
      {!compact && title ? (
        <p className="text-sm font-medium text-brand-dark dark:text-gray-100">{title}</p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={intl.get("baas.entidades.create.identity.number")}
          optional={!docActive}
        >
          <input
            type="text"
            className={inputClass}
            value={doc.number}
            onChange={(event) => onChange({ number: event.target.value })}
            disabled={disabled}
            readOnly={disabled}
          />
        </Field>
        <CountrySelectField
          label={intl.get("baas.entidades.create.identity.countryCode")}
          value={lockedCountry || doc.countryCode}
          onChange={(value) => onChange({ countryCode: value })}
          countryOptions={countryOptions}
          required={docActive && !disabled}
          optional={!docActive || disabled}
          disabled={disabled || Boolean(lockedCountry)}
        />
        <Field
          label={intl.get("baas.entidades.create.identity.expirationDate")}
          optional
        >
          <input
            type="date"
            className={inputClass}
            value={doc.expirationDate}
            onChange={(event) => onChange({ expirationDate: event.target.value })}
            disabled={disabled}
            readOnly={disabled}
          />
        </Field>
        <StateOrTextField
          label={intl.get("baas.entidades.create.identity.issuingRegion")}
          countryCode={lockedCountry || doc.countryCode}
          value={doc.issuingRegion}
          onChange={(value) => onChange({ issuingRegion: value })}
          optional
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export const ReviewSection = ({ title, children, hidden = false, notApplicable = false }) => {
  if (hidden) {
    return null;
  }

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</p>
      <div className="mt-1 space-y-1 text-sm text-gray-600 dark:text-gray-300">
        {notApplicable ? (
          <p className="italic text-gray-400">{intl.get("baas.entidades.create.reviewNotApplicable")}</p>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

const debugTextareaClass =
  "min-h-[120px] w-full flex-1 resize-none rounded border border-gray-200 bg-white px-3 py-2 font-mono text-xs leading-relaxed text-gray-700 outline-none transition focus:border-gray-300 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-300";

const DebugConsolePanel = ({
  jsonText,
  onJsonChange,
  onSyncFromForm,
  onFormatJson,
  onRestoreTemplate,
  onDebugSubmit,
  submitting,
  error,
  labelNamespace = "baas.entidades.create",
}) => (
  <div className="flex min-h-0 flex-1 flex-col space-y-3">
    <p className="text-xs text-gray-500 dark:text-gray-400">
      {intl.get(`${labelNamespace}.debugHint`)}
    </p>
    <div className="flex flex-wrap gap-2">
      <button type="button" className="btn-secondary text-xs" onClick={onSyncFromForm}>
        {intl.get(`${labelNamespace}.debugSyncFromForm`)}
      </button>
      <button type="button" className="btn-secondary text-xs" onClick={onFormatJson}>
        {intl.get(`${labelNamespace}.formatJson`)}
      </button>
      <button type="button" className="btn-secondary text-xs" onClick={onRestoreTemplate}>
        {intl.get(`${labelNamespace}.restoreTemplate`)}
      </button>
    </div>
    <textarea
      className={debugTextareaClass}
      value={jsonText}
      onChange={(event) => onJsonChange(event.target.value)}
      spellCheck={false}
      placeholder={intl.get(`${labelNamespace}.jsonPlaceholder`)}
    />
    {error ? <p className="text-xs font-medium text-red-500">{error}</p> : null}
    <div className="flex justify-end">
      <button
        type="button"
        className="btn-secondary text-xs"
        onClick={onDebugSubmit}
        disabled={submitting}
      >
        {submitting
          ? intl.get(`${labelNamespace}.submitting`)
          : intl.get(`${labelNamespace}.debugSend`)}
      </button>
    </div>
  </div>
);

const DebugConsoleToggleBar = ({ expanded, onToggle, sticky = false, labelNamespace = "baas.entidades.create" }) => (
  <button
    type="button"
    className={sticky ? "baas-debug-console__bar" : "flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left text-xs text-gray-400 transition hover:bg-gray-50 hover:text-gray-600 dark:hover:bg-brand-dark-surface dark:hover:text-gray-300"}
    onClick={onToggle}
    aria-expanded={expanded}
    aria-controls={sticky ? "baas-debug-console-panel" : undefined}
  >
    <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
      {intl.get(`${labelNamespace}.debugConsole`)}
    </span>
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">
      {expanded
        ? intl.get(`${labelNamespace}.debugCollapse`)
        : intl.get(`${labelNamespace}.debugExpand`)}
      {sticky ? (
        expanded ? (
          <TbChevronDown size={14} aria-hidden />
        ) : (
          <TbChevronUp size={14} aria-hidden />
        )
      ) : null}
    </span>
  </button>
);

export const DebugConsoleFooter = ({
  sticky = false,
  expanded,
  onToggle,
  jsonText,
  onJsonChange,
  onSyncFromForm,
  onFormatJson,
  onRestoreTemplate,
  onDebugSubmit,
  submitting,
  error,
  labelNamespace = "baas.entidades.create",
}) => {
  const panelProps = {
    jsonText,
    onJsonChange,
    onSyncFromForm,
    onFormatJson,
    onRestoreTemplate,
    onDebugSubmit,
    submitting,
    error,
    labelNamespace,
  };

  if (!sticky) {
    return (
      <div className="mt-8 border-t border-dashed border-gray-200 pt-4 dark:border-brand-dark-border">
        <DebugConsoleToggleBar expanded={expanded} onToggle={onToggle} labelNamespace={labelNamespace} />
        {expanded ? (
          <div className="mt-3 space-y-3 rounded-lg border border-dashed border-gray-200 bg-gray-50/50 p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface/30">
            <DebugConsolePanel {...panelProps} />
          </div>
        ) : null}
      </div>
    );
  }

  const consoleNode = (
    <div
      className={`baas-debug-console${expanded ? " baas-debug-console--expanded" : ""}`}
      role="region"
      aria-label={intl.get(`${labelNamespace}.debugConsole`)}
    >
      <div
        id="baas-debug-console-panel"
        className="baas-debug-console__panel"
        aria-hidden={!expanded}
      >
        <div className="baas-debug-console__resize-handle" aria-hidden />
        <div className="baas-debug-console__panel-body">
          <DebugConsolePanel {...panelProps} />
        </div>
      </div>
      <DebugConsoleToggleBar expanded={expanded} onToggle={onToggle} sticky labelNamespace={labelNamespace} />
    </div>
  );

  return typeof document !== "undefined" ? createPortal(consoleNode, document.body) : consoleNode;
};
